import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import styled from "styled-components";
import { CaretRight, X } from "@phosphor-icons/react";
import { OrganizationContext } from "../contexts/OrganizationContext";

export default function CreatePrayerPartner(props) {
  const { organizations } = useContext(OrganizationContext);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    organization_name: "",
    roles: "",
    skills: "",
    departments: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [step, setStep] = useState(1); // 1: Email verification, 2: Form completion
  const [userExists, setUserExists] = useState(null); // null: not checked, true: exists, false: doesn't exist
  const [isVerifying, setIsVerifying] = useState(false);

  // Role and skill options loaded from the API (defaults used as fallback)
 
    const [roleOptions, setRoleOptions] = useState([]);
    const [skillOptions, setSkillOptions] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);

  // Search states for dropdowns
  const [orgSearch, setOrgSearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");
  const [skillSearch, setSkillSearch] = useState("");
  const [departmentSearch, setDepartmentSearch] = useState("");
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);

  // Refs for closing dropdowns when clicking outside
  const orgDropdownRef = useRef(null);
  const roleDropdownRef = useRef(null);
  const skillDropdownRef = useRef(null);
  const departmentDropdownRef = useRef(null);
  const orgSearchRef = useRef(null);
  const roleSearchRef = useRef(null);
  const skillSearchRef = useRef(null);
  const departmentSearchRef = useRef(null);

  // Ensures role/skill options are fetched only once
  const optionsLoadedRef = useRef(false);

  useEffect(() => {
    if (props.showLock) {
      handleSignUp();
      props.setShowLock(false);
    }
  }, [props.showLock]);
  
console.log("Modal:", showModal);
console.log("LoadedRef:", optionsLoadedRef.current);
    console.log("API URL:", process.env.REACT_APP_API_URL);

    const normalizeOptions = (list = []) =>
        (Array.isArray(list) ? list : [])
            .sort((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0))
            .map((item) => ({
                value: item.Value,
                label: item.Label ?? item.Value,
            }));
  // Load role and skill options from a single API call when the modal opens
    useEffect(() => {
        console.log("Modal changed:", showModal);
        if (!showModal) return;

        const fetchRoleAndSkillOptions = async () => {
            try {
                console.log("API firing...");

                const apiUrl =
                    process.env.REACT_APP_API_URL + "/master-data/roles-skills?tag=prayer_partner";

                const response = await fetch(apiUrl, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: process.env.REACT_APP_API_AUTHORIZATION,
                        Accept: "application/json",
                    },
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || "Failed to fetch roles/skills");
                }

                const roles = normalizeOptions(data?.data?.roles);
                const skills = normalizeOptions(data?.data?.skills); 
                const departments = normalizeOptions(data?.data?.departments);

                setRoleOptions(roles);
                setSkillOptions(skills);
                setDepartmentOptions(departments);
            } catch (err) {
                console.error("Fetch error:", err);

                setRoleOptions([{ value: "0", label: "---No Records---" }]);
                setSkillOptions([{ value: "0", label: "---No Records---" }]);
                setDepartmentOptions([{ value: "0", label: "---No Records---" }]);
            }
        };

        fetchRoleAndSkillOptions();
    }, [showModal]);

  // ESC key to close modal
  useEffect(() => {
    if (showModal) {
      const handleEscKey = (e) => {
        if (e.key === "Escape") handleCloseModal();
      };
      window.addEventListener("keydown", handleEscKey);
      return () => window.removeEventListener("keydown", handleEscKey);
    }
  });

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        orgDropdownRef.current &&
        !orgDropdownRef.current.contains(event.target) &&
        orgSearchRef.current &&
        !orgSearchRef.current.contains(event.target)
      ) {
        setShowOrgDropdown(false);
        setOrgSearch("");
      }
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target) &&
        roleSearchRef.current &&
        !roleSearchRef.current.contains(event.target)
      ) {
        setShowRoleDropdown(false);
        setRoleSearch("");
      }
      if (
        skillDropdownRef.current &&
        !skillDropdownRef.current.contains(event.target) &&
        skillSearchRef.current &&
        !skillSearchRef.current.contains(event.target)
      ) {
        setShowSkillDropdown(false);
        setSkillSearch("");
      }
      if (
        departmentDropdownRef.current &&
        !departmentDropdownRef.current.contains(event.target) &&
        departmentSearchRef.current &&
        !departmentSearchRef.current.contains(event.target)
      ) {
        setShowDepartmentDropdown(false);
        setDepartmentSearch("");
      }
    };

    if (showOrgDropdown || showRoleDropdown || showSkillDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOrgDropdown, showRoleDropdown, showSkillDropdown, showDepartmentDropdown]);

  const handleSignUp = () => {
    setShowModal(true);
  };

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setFormData({
      email: "",
      password: "",
      full_name: "",
      organization_name: "",
      roles: "",
      skills: "",
      departments: "",
    });
    setErrors({});
    setIsSubmitting(false);
    setOrgSearch("");
    setRoleSearch("");
    setSkillSearch("");
    setDepartmentSearch("");
    setShowOrgDropdown(false);
    setShowRoleDropdown(false);
    setShowSkillDropdown(false);
    setShowDepartmentDropdown(false);
    setApiResponse(null);
    setStep(1);
    setUserExists(null);
    setIsVerifying(false);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleOrgSelect = (orgValue) => {
    setFormData((prev) => ({ ...prev, organization_name: orgValue }));
    setOrgSearch("");
    setShowOrgDropdown(false);
    if (errors.organization_name) {
      setErrors((prev) => ({
        ...prev,
        organization_name: "",
      }));
    }
  };

  const handleRoleSelect = (roleValue) => {
    setFormData((prev) => ({ ...prev, roles: roleValue }));
    setRoleSearch("");
    setShowRoleDropdown(false);
    if (errors.roles) {
      setErrors((prev) => ({
        ...prev,
        roles: "",
      }));
    }
  };

  const handleSkillSelect = (skillValue) => {
    setFormData((prev) => ({ ...prev, skills: skillValue }));
    setSkillSearch("");
    setShowSkillDropdown(false);
    if (errors.skills) {
      setErrors((prev) => ({
        ...prev,
        skills: "",
      }));
    }
  };

  const handleDepartmentSelect = (departmentValue) => {
    setFormData((prev) => ({ ...prev, departments: departmentValue }));
    setDepartmentSearch("");
    setShowDepartmentDropdown(false);
    if (errors.departments) {
      setErrors((prev) => ({
        ...prev,
        departments: "",
      }));
    }
  };

  const convertStringToCamelCase = (sentence) => {
    return sentence.replace(
      /(?:^\w|[A-Z]|\b\w|\s+)/g,
      function (camelCaseMatch, i) {
        if (+camelCaseMatch === 0) return "";
        return i === 0
          ? camelCaseMatch.toLowerCase()
          : camelCaseMatch.toUpperCase();
      },
    );
  };

  const organizationOptions =
    organizations && organizations.length
      ? organizations.map((organization) => ({
          value: convertStringToCamelCase(organization.name),
          label: organization.name,
        }))
      : [{ value: "crossroads", label: "Crossroads" }];

  const filteredOrganizations = organizationOptions.filter((org) =>
    org.label.toLowerCase().includes(orgSearch.toLowerCase()),
  );

  const filteredRoles = roleOptions.filter((role) =>
    role.label.toLowerCase().includes(roleSearch.toLowerCase()),
  );

  const filteredSkills = skillOptions.filter((skill) =>
    skill.label.toLowerCase().includes(skillSearch.toLowerCase()),
  );

  const filteredDepartments = departmentOptions.filter((department) =>
    department.label.toLowerCase().includes(departmentSearch.toLowerCase()),
  );

  const validateEmail = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation (already verified in step 1)
    if (!formData.email) {
      newErrors.email = "Email is required";
    }

    // Only validate password if user doesn't exist
    if (!userExists) {
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters";
      }
    }

    if (!formData.full_name) {
      newErrors.full_name = "Full name is required";
    } else if (formData.full_name.length < 4) {
      newErrors.full_name = "Full name must be at least 4 characters";
    }

    if (!formData.organization_name) {
      newErrors.organization_name = "Please select an organization";
    }

    if (!formData.roles) {
      newErrors.roles = "Please select a role";
    }

    if (!formData.skills) {
      newErrors.skills = "Please select a skill";
    }

    if (!formData.departments) {
      newErrors.departments = "Please select a department";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVerifyEmail = async () => {
    if (!validateEmail()) return;

    setIsVerifying(true);
    setApiResponse(null);

    try {
      console.log("Verifying email:", formData.email);

      // Call the user exists API
      const apiUrl = `${process.env.REACT_APP_API_URL || ""}/auth0/user/exists`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: formData.email }),
      });

      const responseData = await response.json();

      console.log("User exists API Response:", responseData);

      if (!response.ok) {
        throw new Error(responseData.message || "API request failed");
      }

      // Assuming API returns { exists: true/false }
      const exists = responseData.exists || false;
      setUserExists(exists);

      // Move to step 2
      setStep(2);
    } catch (error) {
      console.error("Email verification failed:", error);
      setApiResponse({
        success: false,
        message: error.message || "Failed to verify email. Please try again.",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBackToEmail = () => {
    setStep(1);
    setUserExists(null);
    setApiResponse(null);
    // Keep the email but reset other fields
    setFormData((prev) => ({
      email: prev.email,
      password: "",
      full_name: "",
      organization_name: "",
      roles: "",
      skills: "",
      departments: "",
    }));
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    setApiResponse(null);

    try {
      console.log("Form submitted:", formData);

      // Prepare API request body according to the specification
      const requestBody = {
        email: formData.email,
        name: formData.full_name, // Use full_name for name field
        password: formData.password || undefined, // Only include password if provided
        metadata: {
          department: formData.departments || "Volunteer",
          full_name: formData.full_name,
          organization_name: formData.organization_name,
          roles: formData.roles,
          skills: formData.skills,
        },
      };

      // Remove password field if it's empty (user already exists)
      if (!requestBody.password) {
        delete requestBody.password;
      }

      console.log("API Request Body:", requestBody);

      // Call the API using REACT_APP_API_URL
      const apiUrl = `${process.env.REACT_APP_API_URL || ""}/auth0/user/upsert`;
      console.log("Calling API:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      console.log("API Response:", responseData);

      if (!response.ok) {
        throw new Error(
          responseData.message ||
            `API request failed with status ${response.status}`,
        );
      }

      setApiResponse({
        success: true,
        message:
          responseData.status === "updated"
            ? "Prayer Partner updated successfully!"
            : "Prayer Partner created successfully!",
        data: responseData,
      });

      // Call custom onSubmit prop if provided
      if (props.onSubmit) {
        await props.onSubmit(formData, responseData);
      }

      // Reset form after successful submission
      setFormData({
        email: "",
        password: "",
        full_name: "",
        organization_name: "",
        roles: "",
        skills: "",
        departments: "",
      });

      // Show success message for 3 seconds before closing modal
      setTimeout(() => {
        handleCloseModal();
        if (props.onSuccess) {
          props.onSuccess();
        }
      }, 3000);
    } catch (error) {
      console.error("API Error:", error);

      setApiResponse({
        success: false,
        message:
          error.message ||
          "Failed to create prayer partner account. Please try again.",
      });

      setErrors((prev) => ({
        ...prev,
        submit:
          error.message ||
          "Failed to create prayer partner account. Please try again.",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showModal) return null;

  return (
    <ModalOverlay onClick={handleCloseModal}>
      <ModalContainer
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-prayer-partner-title"
      >
        <ModalHeader>
          <Logo
            src="https://brass-lynx-1229.twil.io/assets/AnyonePray%20Logo-Dark.png"
            alt="AnyonePray Logo"
          />
          <CloseButton
            onClick={handleCloseModal}
            aria-label="Close signup modal"
          >
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <Title id="create-prayer-partner-title">
            {step === 1 ? "Create Prayer Partner" : "Create Prayer Partner"}
          </Title>

          {/* API Response Messages */}
          {apiResponse && (
            <ApiResponseMessage success={apiResponse.success}>
              {apiResponse.message}
            </ApiResponseMessage>
          )}

          {errors.submit && !apiResponse && (
            <SubmitError>{errors.submit}</SubmitError>
          )}

          {/* Step 1: Email Verification */}
          {step === 1 && (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                handleVerifyEmail();
              }}
              noValidate
            >
              <StepDescription>
                Enter the prayer partner's email address to check if they
                already have an account.
              </StepDescription>

              <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="prayerpartner@email.com"
                  error={errors.email}
                  aria-describedby={errors.email ? "email-error" : undefined}
                  aria-invalid={!!errors.email}
                  autoComplete="email"
                  disabled={isVerifying}
                />
                {errors.email && (
                  <ErrorText id="email-error">{errors.email}</ErrorText>
                )}
              </FormGroup>

              <VerifyButton
                type="button"
                onClick={handleVerifyEmail}
                disabled={isVerifying}
                aria-busy={isVerifying}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {isVerifying ? "Verifying..." : "Verify Email"}
                {!isVerifying && <CaretRight size={24} />}
              </VerifyButton>
            </Form>
          )}

          {/* Step 2: Complete Form */}
          {step === 2 && (
            <Form onSubmit={handleSubmit} noValidate>
              {/* Email display (read-only) */}
              <FormGroup>
                <Label>Email Address</Label>
                <EmailDisplay>
                  <EmailText>{formData.email}</EmailText>
                  <EmailStatus userExists={userExists}>
                    {userExists ? "Existing User" : "New Prayer Partner"}
                  </EmailStatus>
                </EmailDisplay>
                <BackLink onClick={handleBackToEmail}>Change email</BackLink>
              </FormGroup>

              {/* Password field - only show if user doesn't exist */}
              {!userExists && (
                <FormGroup>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="partner's default password"
                    error={errors.password}
                    aria-describedby={
                      errors.password ? "password-error" : undefined
                    }
                    aria-invalid={!!errors.password}
                    autoComplete="new-password"
                    disabled={isSubmitting || apiResponse?.success}
                  />
                  {errors.password && (
                    <ErrorText id="password-error">{errors.password}</ErrorText>
                  )}
                </FormGroup>
              )}

              <FormGroup>
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Enter Partner's Full Name"
                  error={errors.full_name}
                  aria-describedby={
                    errors.full_name ? "full_name-error" : undefined
                  }
                  aria-invalid={!!errors.full_name}
                  autoComplete="name"
                  disabled={isSubmitting || apiResponse?.success}
                />
                {errors.full_name && (
                  <ErrorText id="full_name-error">{errors.full_name}</ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="organization_name">Organization</Label>
                <DropdownContainer>
                  <SearchableSelect
                    ref={orgSearchRef}
                    type="text"
                    value={
                      showOrgDropdown
                        ? orgSearch
                        : organizationOptions.find(
                            (org) => org.value === formData.organization_name,
                          )?.label || ""
                    }
                    onChange={(e) => {
                      setOrgSearch(e.target.value);
                      if (!showOrgDropdown) setShowOrgDropdown(true);
                    }}
                    onClick={() => setShowOrgDropdown(true)}
                    placeholder="Select Organization Name"
                    error={errors.organization_name}
                    readOnly={
                      !showOrgDropdown || isSubmitting || apiResponse?.success
                    }
                    isOpen={showOrgDropdown}
                    disabled={isSubmitting || apiResponse?.success}
                  />
                  <DropdownIcon isOpen={showOrgDropdown}>▼</DropdownIcon>
                  {showOrgDropdown && (
                    <DropdownMenu ref={orgDropdownRef}>
                      <OptionsList>
                        {filteredOrganizations.length > 0 ? (
                          filteredOrganizations.map((org, i) => (
                            <OptionItem
                              key={i}
                              onClick={() => handleOrgSelect(org.value)}
                              selected={
                                formData.organization_name === org.value
                              }
                            >
                              {org.label}
                            </OptionItem>
                          ))
                        ) : (
                          <NoResults>No organizations found</NoResults>
                        )}
                      </OptionsList>
                    </DropdownMenu>
                  )}
                </DropdownContainer>
                {errors.organization_name && (
                  <ErrorText id="org-error">
                    {errors.organization_name}
                  </ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="roles">Partner's Role</Label>
                <DropdownContainer>
                  <SearchableSelect
                    ref={roleSearchRef}
                    type="text"
                    value={
                      showRoleDropdown
                        ? roleSearch
                        : roleOptions.find(
                            (role) => role.value === formData.roles,
                          )?.label || ""
                    }
                    onChange={(e) => {
                      setRoleSearch(e.target.value);
                      if (!showRoleDropdown) setShowRoleDropdown(true);
                    }}
                    onClick={() => setShowRoleDropdown(true)}
                    placeholder="Select Partner's Role"
                    error={errors.roles}
                    readOnly={
                      !showRoleDropdown || isSubmitting || apiResponse?.success
                    }
                    isOpen={showRoleDropdown}
                    disabled={isSubmitting || apiResponse?.success}
                  />
                  <DropdownIcon isOpen={showRoleDropdown}>▼</DropdownIcon>
                  {showRoleDropdown && (
                    <DropdownMenu ref={roleDropdownRef}>
                      <OptionsList>
                        {filteredRoles.length > 0 ? (
                          filteredRoles.map((role, i) => (
                            <OptionItem
                              key={i}
                              onClick={() => handleRoleSelect(role.value)}
                              selected={formData.roles === role.value}
                            >
                              {role.label}
                            </OptionItem>
                          ))
                        ) : (
                          <NoResults>No roles found</NoResults>
                        )}
                      </OptionsList>
                    </DropdownMenu>
                  )}
                </DropdownContainer>
                {errors.roles && (
                  <ErrorText id="role-error">{errors.roles}</ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="skills">Partner's Skill</Label>
                <DropdownContainer>
                  <SearchableSelect
                    ref={skillSearchRef}
                    type="text"
                    value={
                      showSkillDropdown
                        ? skillSearch
                        : skillOptions.find(
                            (skill) => skill.value === formData.skills,
                          )?.label || ""
                    }
                    onChange={(e) => {
                      setSkillSearch(e.target.value);
                      if (!showSkillDropdown) setShowSkillDropdown(true);
                    }}
                    onClick={() => setShowSkillDropdown(true)}
                    placeholder="Select Partner's Skill"
                    error={errors.skills}
                    readOnly={
                      !showSkillDropdown || isSubmitting || apiResponse?.success
                    }
                    isOpen={showSkillDropdown}
                    disabled={isSubmitting || apiResponse?.success}
                  />
                  <DropdownIcon isOpen={showSkillDropdown}>▼</DropdownIcon>
                  {showSkillDropdown && (
                    <DropdownMenu ref={skillDropdownRef}>
                      <OptionsList>
                        {filteredSkills.length > 0 ? (
                          filteredSkills.map((skill, i) => (
                            <OptionItem
                              key={i}
                              onClick={() => handleSkillSelect(skill.value)}
                              selected={formData.skills === skill.value}
                            >
                              {skill.label}
                            </OptionItem>
                          ))
                        ) : (
                          <NoResults>No skills found</NoResults>
                        )}
                      </OptionsList>
                    </DropdownMenu>
                  )}
                </DropdownContainer>
                {errors.skills && (
                  <ErrorText id="skill-error">{errors.skills}</ErrorText>
                )}
                          </FormGroup>

              <FormGroup>
                <Label htmlFor="departments">Department</Label>
                <DropdownContainer>
                  <SearchableSelect
                    ref={departmentSearchRef}
                    type="text"
                    value={
                      showDepartmentDropdown
                        ? departmentSearch
                        : departmentOptions.find(
                            (department) => department.value === formData.departments,
                          )?.label || ""
                    }
                    onChange={(e) => {
                      setDepartmentSearch(e.target.value);
                      if (!showDepartmentDropdown) setShowDepartmentDropdown(true);
                    }}
                    onClick={() => setShowDepartmentDropdown(true)}
                    placeholder="Select Partner's Department"
                    error={errors.departments}
                    readOnly={
                      !showDepartmentDropdown || isSubmitting || apiResponse?.success
                    }
                    isOpen={showDepartmentDropdown}
                    disabled={isSubmitting || apiResponse?.success}
                  />
                  <DropdownIcon isOpen={showDepartmentDropdown}>▼</DropdownIcon>
                  {showDepartmentDropdown && (
                    <DropdownMenu ref={departmentDropdownRef}>
                      <OptionsList>
                        {filteredDepartments.length > 0 ? (
                          filteredDepartments.map((department, i) => (
                            <OptionItem
                              key={i}
                              onClick={() => handleDepartmentSelect(department.value)}
                              selected={formData.departments === department.value}
                            >
                              {department.label}
                            </OptionItem>
                          ))
                        ) : (
                          <NoResults>No departments found</NoResults>
                        )}
                      </OptionsList>
                    </DropdownMenu>
                  )}
                </DropdownContainer>
                {errors.departments && (
                  <ErrorText id="department-error">{errors.departments}</ErrorText>
                )}
              </FormGroup>

              <Policy>
                By signing up, you agree to our terms of service and privacy
                policy.
              </Policy>

              <SubmitButton
                type="submit"
                disabled={isSubmitting || apiResponse?.success}
                aria-busy={isSubmitting}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {isSubmitting
                  ? "Submitting..."
                  : apiResponse?.success
                    ? userExists
                      ? "Account Updated!"
                      : "Created!"
                    : userExists
                      ? "Update Prayer Partner"
                      : "Create New Prayer Partner"}
                {!apiResponse?.success && <CaretRight size={24} />}
              </SubmitButton>
            </Form>
          )}
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
}

// Styled Components - Updated with two-step workflow components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 430px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &:focus {
    outline: none;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: #eaeaea;
  position: relative;
`;

const Logo = styled.img`
  height: 40px;
  width: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    color: #333;
    background-color: rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: 2px solid #2db3c0;
    outline-offset: 2px;
  }
`;

const ModalContent = styled.div`
  padding: 20px;
`;

const Title = styled.h2`
  margin-bottom: 20px;
  text-align: center;
  font-size: 24px;
  color: #ec3310;
  font-family: Recoleta;
  text-decoration: underline;
`;

const StepDescription = styled.p`
  margin-bottom: 20px;
  text-align: center;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
`;

const ProgressIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  gap: 8px;
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  opacity: ${(props) => (props.active ? "1" : "0.5")};
`;

const StepNumber = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${(props) => (props.active ? "#2db3c0" : "#ddd")};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const StepLabel = styled.div`
  font-size: 12px;
  color: #666;
  white-space: nowrap;
`;

const ProgressLine = styled.div`
  width: 40px;
  height: 2px;
  background-color: #2db3c0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Label = styled.label`
  margin-bottom: 6px;
  font-weight: 500;
  color: #555;
  font-size: 14px;
  font-family: "Futura PT", sans-serif;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid ${(props) => (props.error ? "#e74c3c" : "#ddd")};
  border-radius: 4px;
  font-size: 16px;
  transition: all 0.2s;
  font-family: "Futura PT", sans-serif;

  &:focus {
    outline: none;
    border-color: #2db3c0;
    box-shadow: 0 0 0 3px rgba(45, 179, 192, 0.2);
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #999;
  }
`;

const EmailDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #2db3c0;
  border-radius: 4px;
  background-color: #e6f7f9;
`;

const EmailText = styled.span`
  font-weight: 500;
  color: #333;
`;

const EmailStatus = styled.span`
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  background-color: ${(props) => (props.userExists ? "#ffeaa7" : "#a5d6a7")};
  color: ${(props) => (props.userExists ? "#e17055" : "#2e7d32")};
  font-weight: 500;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #2db3c0;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
  padding: 4px 0;
  align-self: flex-start;
  margin-top: 4px;

  &:hover {
    color: #249ba6;
  }
`;

const VerifyButton = styled.button`
  background-color: #2db3c0;
  color: white;
  border: none;
  padding: 14px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 10px;
  font-family: "Space Grotesk", sans-serif;
  letter-spacing: 0.15em;
  display: flex;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: #249ba6;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    opacity: 0.7;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(45, 179, 192, 0.4);
  }
`;

const SubmitButton = styled(VerifyButton)`
  // Inherits all styles from VerifyButton
`;

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchableSelect = styled(Input)`
  width: 100%;
  cursor: pointer;
  background-color: white;
  position: relative;
  z-index: 2;
  padding-right: 36px;
  user-select: none;

  &[readonly] {
    cursor: pointer;
    background-color: white;
  }

  &:focus {
    border-color: ${(props) => (props.isOpen ? "#2db3c0" : "#ddd")};
    box-shadow: ${(props) =>
      props.isOpen ? "0 0 0 3px rgba(45, 179, 192, 0.2)" : "none"};
  }
`;

const DropdownIcon = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%)
    ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  font-size: 10px;
  color: #666;
  pointer-events: none;
  z-index: 3;
  transition: transform 0.2s ease;
`;

const DropdownMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #2db3c0;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 250px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: dropdownSlideUp 0.2s ease-out;

  @keyframes dropdownSlideUp {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const OptionsList = styled.div`
  max-height: 250px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #999;
  }
`;

const OptionItem = styled.div`
  padding: 10px 14px;
  cursor: pointer;
  font-family: "Futura PT", sans-serif;
  font-size: 14px;
  background-color: ${(props) => (props.selected ? "#e6f7f9" : "white")};
  color: ${(props) => (props.selected ? "#2db3c0" : "#333")};
  font-weight: ${(props) => (props.selected ? "500" : "normal")};
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${(props) => (props.selected ? "#d0f0f5" : "#f8f9fa")};
    color: ${(props) => (props.selected ? "#2db3c0" : "#2db3c0")};
  }

  &:active {
    background-color: #c0eef3;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NoResults = styled.div`
  padding: 16px 14px;
  text-align: center;
  color: #999;
  font-family: "Futura PT", sans-serif;
  font-size: 14px;
  font-style: italic;
`;

const ErrorText = styled.span`
  color: #e74c3c;
  font-size: 13px;
  margin-top: 4px;
  display: block;
`;

const SubmitError = styled.div`
  background-color: #fee;
  color: #c33;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
  text-align: center;
  border: 1px solid #fcc;
`;

const ApiResponseMessage = styled.div`
  background-color: ${(props) => (props.success ? "#d4edda" : "#f8d7da")};
  color: ${(props) => (props.success ? "#155724" : "#721c24")};
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
  text-align: center;
  border: 1px solid ${(props) => (props.success ? "#c3e6cb" : "#f5c6cb")};
  animation: fadeIn 0.3s ease-out;
`;

const Policy = styled.div`
  background-color: #eaeaea;
  padding: 5px;
  text-align: center;
`;
